/**
 * The two ways a violation reaches a facility. Mirrors the join each harmonize
 * stage actually performs, which is why the review record is keyed on this
 * rather than on the alternate-id type: the waste stage collapses 'EPA' and
 * 'RCRAINFO' into one match group, so keying on type would produce two
 * candidate records for a single violation.
 *
 * Lives here rather than in the harmonize task because both sides of the review
 * gate need it — the task writes the records, and the staff queue reads back the
 * alternate-id rows behind each one.
 *
 * Deliberately a sibling module with no barrel export, for the same reason as
 * `visibleHarmonizedViolationWhere`.
 */
export type ExpiredAltIdMatchKindLiteral = "WASTE_HANDLER" | "FRS_REGISTRY" | "SHARED_FRS";

/**
 * The `FacilityAlternateId.type` values a match kind can resolve through.
 *
 * FRS_REGISTRY covers water AND air: both match an FRS-typed value against
 * `registryId`, so one record — and one acceptance — releases both programs'
 * rows for that facility. That is a property of how EPA identifies facilities,
 * not a simplification made here.
 *
 * SHARED_FRS is the cross-program path: the facility's FRS registry — resolved
 * from a sibling program id (RCRA handler, EPA id, NPDES permit) through
 * EchoExporter — matched a violation the facility's own program ids do not.
 * A separate kind — not folded into FRS_REGISTRY — so staff accept
 * prior-owner matches deliberately, never as a side effect of accepting a
 * direct one.
 *
 * BOTH FRS_REGISTRY and SHARED_FRS key their review record's altIdValue on
 * the FRS REGISTRY id — a key that stays stable while alternate ids come and
 * go, so acceptance is durable. The API's review queue and un-expire guard
 * resolve either key back to the contributing FacilityAlternateId rows
 * through EchoExporter containment (sharedFrsGroupResolution.ts), mirroring
 * the job's release rule (any contributing id becomes active). The type list
 * below is the value-equality fallback surface (e.g. the Oak link warning,
 * which cannot do containment client-side).
 */
export function altIdTypesFor(kind: ExpiredAltIdMatchKindLiteral): string[] {
  if (kind === "WASTE_HANDLER") {
    return ["RCRAINFO", "EPA"];
  }
  if (kind === "SHARED_FRS") {
    return ["RCRAINFO", "EPA", "NPDES", "FRS"];
  }
  return ["FRS"];
}

/**
 * `extensions.code` on the refusal thrown when a save would un-expire an
 * alternate id whose violations are held for staff review.
 *
 * Shared because both sides need it: the API stamps it on the error, and Oak
 * keeps that refusal on screen instead of letting it auto-dismiss — it names a
 * remedy the reader has to act on.
 */
export const HELD_ALTERNATE_ID_ERROR_CODE = "HELD_EXPIRED_ALT_ID";
